import { Directive, inject, input, OnChanges, OnDestroy, OnInit, OutputRefSubscription, SimpleChanges } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { finalize, forkJoin, map, Observable, Subscription, tap } from 'rxjs';
import { groupBy } from 'lodash';
import { SvcQuillTextEditorComponent, SvcQuillTextEditorMention, SvcQuillTextEditorMentionEvent } from '@solvace/component/svc-controls/svc-quill-text-editor';
import { SvcQuillEditorMentionService } from './svc-quill-editor-mention.service';
import { MentionTeam, MentionUser } from './svc-quill-editor-mention.model';

export enum SvcCommentMentionType {
  USER = 1,
  TEAM = 2,
  HASHTAG = 3,
}

export interface Mention extends SvcQuillTextEditorMention {
  disabled?: boolean;
  target?: string;
  type?: SvcCommentMentionType;
  siteId?: number;
}

@Directive({
  selector: '[svcQuillEditorMention]',
  providers: [SvcQuillEditorMentionService],
})
export class SvcQuillEditorMentionDirective implements OnInit, OnChanges, OnDestroy {
  allowUsers = input(true);
  allowTeams = input(true);
  allowHashtags = input(true);

  #service = inject(SvcQuillEditorMentionService);
  #transloco = inject(TranslocoService);
  #svcQuillTextEditor = inject(SvcQuillTextEditorComponent);
  #mentionIsLoading = false;
  #mentionSubscription: Subscription;
  #unsubscribeTextEditor: OutputRefSubscription;

  public ngOnInit(): void {
    this.#unsubscribeTextEditor = this.#svcQuillTextEditor?.onMentionTriggered.subscribe((event) => {
      this.#onMentionTriggered(event);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('allowUsers' in changes || 'allowTeams' in changes || 'allowHashtags' in changes) {
      this.#svcQuillTextEditor?.allowMentionAt.update(() => this.allowUsers() || this.allowTeams());
      this.#svcQuillTextEditor?.allowMentionHashtag.update(() => this.allowHashtags());
      this.#svcQuillTextEditor?.checkMentionCharacters();
    }
  }

  #onMentionTriggered(options: SvcQuillTextEditorMentionEvent<Mention>): void {
    const { searchTerm, setList, mentionChar } = options;
    this.#mentionSubscription?.unsubscribe();
    const isHashtag = mentionChar === '#';
    const isUserOrTeam = mentionChar === '@';

    if ((isHashtag && !this.allowHashtags()) || (isUserOrTeam && !this.allowUsers() && !this.allowTeams()) || searchTerm.length < 3) {
      this.#mentionIsLoading = false;
      this.#setIsQuillMentionLoading();
      return;
    }

    this.#mentionIsLoading = true;
    this.#setIsQuillMentionLoading();
    if (mentionChar === '@' && (this.allowUsers() || this.allowTeams())) {
      this.#mentionSubscription = this.#mentionUserAndTeam(searchTerm)
        .pipe(
          tap((results) => {
            const mentions: Mention[] = [];
            const teamUsersGrouped = groupBy([...results.users, ...results.teams], 'site');
            for (const site of Object.keys(teamUsersGrouped).sort((a) => (a === 'Multi-Tenance' ? 1 : -1))) {
              const list = teamUsersGrouped[site];
              mentions.push({
                value: this.#transloco.translate(site == 'Multi-Tenance' ? 'Global' : 'Planta'),
                disabled: true,
                target: 'level-1',
              });
              const grouped = groupBy(list, 'type');
              for (const type in grouped) {
                const list = grouped[type] as Partial<{
                  type: string;
                  teamId: number;
                  userId: string;
                  name: string;
                }>[];
                mentions.push({
                  value: this.#transloco.translate(type == 'user' ? 'Usuários' : 'Times'),
                  disabled: true,
                  target: 'level-2',
                });
                list.forEach((d) =>
                  mentions.push({
                    id: d.userId || d.teamId,
                    value: d.name,
                    type: type == 'user' ? 1 : 2,
                  })
                );
              }
            }
            setList(mentions);
          }),
          finalize(() => (this.#mentionIsLoading = false))
        )
        .subscribe();
    } else if (mentionChar === '#' && this.allowHashtags()) {
      this.#mentionSubscription = this.#service
        .getHashtags(searchTerm)
        .pipe(
          tap((list) => {
            const mentions = [];
            const hashtagsGrouped = groupBy(list, 'isGlobal');
            for (const isGlobal in hashtagsGrouped) {
              const list = hashtagsGrouped[isGlobal];
              mentions.push({
                value: this.#transloco.translate(isGlobal == 'true' ? 'Global' : 'Planta'),
                disabled: true,
                target: 'level-1',
              });
              list.forEach((h) =>
                mentions.push({
                  id: h.hashtagId,
                  value: h.name.replace('#', ''),
                  siteId: h.siteId,
                })
              );
            }
            setList(mentions);
          }),
          finalize(() => (this.#mentionIsLoading = false))
        )
        .subscribe();
    }
  }

  #mentionUserAndTeam(name: string): Observable<{
    teams: {
      type: string;
      teamId: number;
      name: string;
      siteId: number;
      site: string;
    }[];
    users: {
      type: string;
      userId: string;
      site: string;
    }[];
  }> {
    const requests: { users?: Observable<MentionUser[]>; teams?: Observable<MentionTeam[]> } = {};
    if (this.allowUsers()) {
      requests.users = this.#service.getUsers(name);
    }
    if (this.allowTeams()) {
      requests.teams = this.#service.getTeams(name);
    }
    return forkJoin(requests).pipe(
      map((response) => {
        return {
          teams:
            response?.teams?.map((t) => ({
              type: 'team',
              teamId: t.teamId,
              name: t.name,
              siteId: t.siteId,
              site: t.siteName !== this.#service.currenSiteName ? 'Multi-Tenance' : t.siteName,
            })) ?? [],
          users:
            response?.users?.map((u) => ({
              type: 'user',
              ...u,
              site: u.site !== this.#service.currenSiteName ? 'Multi-Tenance' : u.site,
            })) ?? [],
        };
      })
    );
  }

  #setIsQuillMentionLoading(): void {
    const mentionLoading = document.querySelector('.ql-mention-list-container .ql-mention-loading');
    if (mentionLoading) {
      if (this.#mentionIsLoading) {
        mentionLoading.innerHTML = this.#transloco.translate('Pesquisando...');
      } else {
        mentionLoading.innerHTML = this.#transloco.translate('Digite ao menos 3 caracteres');
      }
    }
  }

  public ngOnDestroy(): void {
    this.#unsubscribeTextEditor?.unsubscribe();
  }
}
